import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiFileText, FiCheckCircle, FiAlertCircle, FiDollarSign, FiShield, FiUsers, FiArrowUp } from 'react-icons/fi';
import './LegalPages.css';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="legal-page-wrapper">
      <div className="legal-page-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back to Home
        </button>
        <div className="legal-page-title">
          <FiFileText className="legal-icon" />
          <h1>Terms & Conditions</h1>
        </div>
        <p className="legal-page-subtitle">
          These Terms & Conditions ("Terms") govern your access to and use of www.lumesolutions.co (the "Site") 
          and any services, features, or content offered through it (collectively, "Services") by Lume Solutions 
          (or "we", "us", "our"). By accessing or using the Site or Services, you agree to be bound by these Terms. 
          If you disagree with any part, please do not use the Site.
        </p>
        <p className="legal-last-updated">Last Updated: October 2025</p>
      </div>

      <div className="legal-content-container">
        <section className="legal-section">
          <div className="section-header">
            <FiCheckCircle className="section-icon" />
            <h2>1. Eligibility & Acceptance</h2>
          </div>
          <ul className="legal-list">
            <li>You must be at least 18 years old and capable of entering a binding contract.</li>
            <li>By using the Site, you confirm that the information you provide is true, accurate, current, and complete.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiUsers className="section-icon" />
            <h2>2. Account & Registration</h2>
          </div>
          <ul className="legal-list">
            <li>Some parts of the Site or Services may require you to register for an account.</li>
            <li>You are responsible for safeguarding your login credentials, and for any activity that occurs under your account.</li>
            <li>Notify us immediately of any unauthorized use of your account.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiCheckCircle className="section-icon" />
            <h2>3. Use of Services & Products</h2>
          </div>
          <ul className="legal-list">
            <li>The Site may offer various services, tools, software, or products. Use of each may be governed by additional rules and agreements.</li>
            <li>We may accept, reject, suspend, or terminate your eligibility to access or use any Service at our sole discretion.</li>
            <li>You agree to use the Services in compliance with applicable laws and in good faith.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiDollarSign className="section-icon" />
            <h2>4. Payment, Fees & Billing</h2>
          </div>
          <ul className="legal-list">
            <li>If the Site offers paid services, you agree to pay all applicable fees.</li>
            <li>You authorize us to charge your chosen payment method for the fees.</li>
            <li>All payments are non-refundable unless expressly stated otherwise.</li>
            <li>We may change fees or billing methods at any time, with prior notice.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiShield className="section-icon" />
            <h2>5. Intellectual Property / Content</h2>
          </div>
          <ul className="legal-list">
            <li>All content on the Site (text, images, graphics, software) is owned by us or our licensors and is protected by intellectual property laws.</li>
            <li>You may not reproduce, distribute, or create derivative works from the content without prior permission.</li>
            <li>You grant us a license to use any content you upload or submit via the Site (e.g. for display, promotion) in connection with the Services.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiAlertCircle className="section-icon" />
            <h2>6. Prohibited Uses</h2>
          </div>
          <p>You shall not:</p>
          <ul className="legal-list">
            <li>Use the Site for any unlawful purpose or in violation of any regulations.</li>
            <li>Interfere with the operation of the Site (e.g. via hacking, interfering with traffic).</li>
            <li>Use bots, scrapers, or automated tools to access or collect data from the Site without permission.</li>
            <li>Impersonate others or attempt to disguise identity.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiFileText className="section-icon" />
            <h2>7. Third-Party Links & Services</h2>
          </div>
          <p>
            The Site may include links to third-party websites, services, or integrations. We are not responsible 
            for their content, policies, or practices.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiAlertCircle className="section-icon" />
            <h2>8. Disclaimers & Warranties</h2>
          </div>
          <ul className="legal-list">
            <li>The Site and Services are provided "as is" and "as available," without warranties of any kind.</li>
            <li>We do not guarantee that the Site will be error-free, secure, or uninterrupted.</li>
          </ul>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiShield className="section-icon" />
            <h2>9. Limitation of Liability</h2>
          </div>
          <p>
            To the fullest extent permitted by law, we (and our directors, employees, agents) will not be liable 
            for any indirect, incidental, special, or consequential losses (e.g. loss of data, profits, business 
            interruption) arising out of your use of the Site or Services. Our aggregate liability to you will 
            not exceed the total amount you paid (if any) for the Service that caused the liability.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiShield className="section-icon" />
            <h2>10. Indemnification</h2>
          </div>
          <p>
            You agree to indemnify and hold us harmless from any claims, liability, losses, damages, or costs 
            (including legal fees) arising out of your violation of these Terms or your misuse of the Services.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiAlertCircle className="section-icon" />
            <h2>11. Termination</h2>
          </div>
          <p>
            We may suspend or terminate your access to the Site or Services, without prior notice, if you breach 
            these Terms or for any other reason. On termination, your rights under these Terms immediately cease.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiFileText className="section-icon" />
            <h2>12. Changes to Terms</h2>
          </div>
          <p>
            We may modify these Terms at any time. We will post the revised version on the Site and update the 
            "Last Updated" date. Your continued use of the Site after that constitutes acceptance of the modified Terms.
          </p>
        </section>

        <section className="legal-section">
          <div className="section-header">
            <FiCheckCircle className="section-icon" />
            <h2>13. Governing Law & Dispute Resolution</h2>
          </div>
          <p>
            These Terms are governed by the laws of Kenya where Lume Solutions is registered. Any dispute will 
            be resolved in the competent courts of Nairobi, Kenya.
          </p>
        </section>

        <section className="legal-section contact-section">
          <div className="section-header">
            <FiUsers className="section-icon" />
            <h2>14. Contact Information</h2>
          </div>
          <p>If you have any questions about these Terms, you may contact us at:</p>
          <div className="contact-box">
            <h3>Lume Solutions Limited</h3>
            <p>8th Floor, Reliable Towers, Mogotio Road, Westlands, Nairobi</p>
            <p>P.O BOX 26541-00504, Nairobi Kenya</p>
            <p>
              <strong>Email:</strong> <a href="mailto:info@lumesolutions.co">info@lumesolutions.co</a>
            </p>
            <p><strong>Phone:</strong> +254 716 060 606</p>
          </div>
        </section>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          className="back-to-top-btn"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <FiArrowUp />
        </button>
      )}
    </div>
  );
};

export default TermsAndConditions;